import React, { useContext, useState } from 'react';
import { List, Action } from '@op/opds.opux';
import { StateContext, ChangeStateContext } from '../../../State';
import { RepMode, PERSON_REPRESENTATION_TYPES, COMPANY_REPRESENTATION_TYPES, LoginType } from '../../../constants';
import RepresentationRow from './RepresentationRow';
import { Message, useMessageGetter } from '@messageformat/react';
import CompanySearch from './CompanySearch';
import { space } from '@op/opds.tokens';
import { IconPrevious } from '@op/opds.opux-icons/navigation';
import { ModelContext } from '../../../ModelContext';

const RepresentationList = ({ representations, selectRepresentation, onBackButtonClicked }) => {
    const state = useContext(StateContext);
    const changeState = useContext(ChangeStateContext);
    const [searchTerm, setSearchTerm] = useState('');
    const msg = useMessageGetter('fp');
    const { model } = useContext(ModelContext);

    const onClickRepresentation = (rep) => {
        changeState({ repMode: RepMode.LIST_SELECT });
        selectRepresentation(rep);
    };

    const getCompanyRepresentations = () => {
        const companyReps = representations.filter((rep) => {
            return COMPANY_REPRESENTATION_TYPES.includes(rep.representationType);
        });
        for (const companyRep of companyReps) {
            /* Enrich data by agreementCount (=number of agreements for this company id). Used to show extra information in the list. */
            companyRep.agreementCount = representations.filter((otherRep) => {
                return companyRep.repCustomerID === otherRep.repCustomerID;
            }).length;
        }
        return companyReps;
    };

    const matchesSearch = (rep) => {
        return (
            !searchTerm || rep.representationName.toLowerCase().search(searchTerm.toLowerCase()) !== -1 || rep.repCustomerID.search(searchTerm) !== -1
        );
    };

    const personRepresentations = representations.filter((rep) => {
        return PERSON_REPRESENTATION_TYPES.includes(rep.representationType);
    });

    const companyRepresentations = getCompanyRepresentations();

    const companyRepresentationsMatchingSearch = companyRepresentations.filter((rep) => {
        return matchesSearch(rep);
    });

    const titleText = model.loginType !== LoginType.CHANGEREP ? msg('representation.title') : msg('representation.titleChange');

    const currentRepresentationName = model.currentRepresentation?.representationName ? model.currentRepresentation?.representationName : '?';

    let statusKey;
    if (personRepresentations.length === 0) {
        statusKey = 'accessibility.representationlist.noPersonManyCompanies';
    } else if (companyRepresentations.length === 1) {
        statusKey = 'accessibility.representationlist.onePersonOneCompany';
    } else {
        statusKey = 'accessibility.representationlist.onePersonManyCompanies';
    }
    const mainStatusMessage = msg(statusKey).toString().replaceAll('[COUNT]', companyRepresentations.length);
    if (mainStatusMessage !== state.statusMessage) {
        changeState({ statusMessage: mainStatusMessage });
    }

    return (
        <div id='representation-list-container'>
            <h2 style={{ marginBottom: space.small }}>{titleText}</h2>
            {model.loginType === LoginType.CHANGEREP && (
                <div style={{ marginBottom: space.large }}>
                    <Action onClick={onBackButtonClicked} iconPos='left' icon={IconPrevious} size='small'>
                        <Message id='fp.representation.back' /> (<Message id='fp.representation.current' />: {currentRepresentationName})
                    </Action>
                </div>
            )}
            {personRepresentations.length > 0 && (
                <>
                    <h3 id='person-representations-heading'>
                        <Message id='fp.representation.privateCustomer' />
                    </h3>
                    <List aria-labelledby='person-representations-heading'>
                        {personRepresentations.map((rep) => (
                            <RepresentationRow key={rep.encryptedSelectString} rep={rep} onClick={onClickRepresentation} />
                        ))}
                    </List>
                </>
            )}
            {companyRepresentations.length > 0 && (
                <>
                    <h3 id='company-representations-heading'>
                        <Message id='fp.representation.companyCustomer' />
                    </h3>
                    {companyRepresentations.length >= 10 && <CompanySearch setSearchTerm={setSearchTerm} />}
                    {companyRepresentationsMatchingSearch.length > 0 && (
                        <List aria-labelledby='company-representations-heading'>
                            {companyRepresentationsMatchingSearch.map((rep) => (
                                <RepresentationRow key={rep.encryptedSelectString} rep={rep} onClick={onClickRepresentation} />
                            ))}
                        </List>
                    )}
                    {companyRepresentationsMatchingSearch.length === 0 && (
                        <div role='alert' className='ds-combobox__no-results'>
                            <Message id='fp.representation.search.noResults' />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default RepresentationList;
